import React from 'react';
import { Link } from 'react-router-dom';
import { Event } from '../types';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../hooks/useCategories';

interface EventCardProps {
  readonly event: Event;
  readonly variant?: 'horizontal' | 'card';
}

const EventCard: React.FC<EventCardProps> = ({ event, variant = 'card' }) => {
  const { t } = useTranslation();
  const { getCategoryName } = useCategories();
  const formattedDate = format(parseISO(event.date), 'dd MMM, yyyy');

  const priceLabel =
    event.price == null ? null :
    event.price === 0 ? t('events.free') :
    `R$ ${event.price}`;

  if (variant === 'horizontal') {
    return (
      <Link to={`/events/${event.id}`} className="flex gap-4 bg-surface p-3 rounded-xl border border-outline-variant/30 shadow-sm hover:bg-surface-container transition-colors group">
        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="flex-1 flex flex-col justify-between py-1 min-w-0">
          <div>
            <h4 className="font-headline text-label-md text-on-surface line-clamp-1">{event.title}</h4>
            <p className="text-label-sm text-on-surface-variant flex items-center gap-1 mt-1">
              <span className="material-symbols-outlined text-[14px]">calendar_today</span>
              {formattedDate}
            </p>
            <p className="text-label-sm text-on-surface-variant flex items-center gap-1 mt-0.5">
              <span className="material-symbols-outlined text-[14px]">location_on</span>
              {event.venue}, {event.city}
            </p>
          </div>
          <div className="flex justify-between items-center mt-2">
            {priceLabel != null && (
              <span className="text-primary font-bold text-label-md">{priceLabel}</span>
            )}
            <span className="material-symbols-outlined text-secondary text-[20px]">chevron_right</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/events/${event.id}`} className="group flex-shrink-0 w-full bg-surface-container-lowest card-elevation overflow-hidden border border-outline-variant/30 transition-all duration-300 hover:-translate-y-1">
      <div className="relative aspect-[1600/838] overflow-hidden">
        <img
          src={event.imageUrl}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 bg-secondary text-on-secondary font-headline text-label-sm px-2 py-1 rounded-md shadow-md">
          {getCategoryName(event.category)}
        </div>
        {priceLabel != null && (
          <div className="absolute bottom-3 right-3 price-tag shadow-lg">
            {priceLabel}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-headline text-[20px] font-bold mb-1 truncate text-on-surface">{event.title}</h3>
        <div className="flex items-center gap-1 text-on-surface-variant text-label-sm">
          <span className="material-symbols-outlined text-sm">location_on</span>
          <span>{event.venue}, {event.city}</span>
        </div>
        <div className="flex items-center gap-1 text-on-surface-variant text-label-sm mt-1">
          <span className="material-symbols-outlined text-sm">calendar_today</span>
          <span>{formattedDate} • {event.time}</span>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
